import type { BraintreeCard } from "./paws/braintreeCC/types";

/**
 * Converts UWT / BRT `lastUsedDateTime` values to comparable epoch milliseconds.
 *
 * - ISO strings (UWT): parsed with `Date.parse`.
 * - Numbers: values with absolute magnitude < 1e12 are treated as **Unix seconds**
 *   (BRT); otherwise **milliseconds** (typical JS timestamps from ~2001 onward).
 * - Numeric strings: parsed after ISO attempt.
 */
export const lastUsedDateTimeToMillis = (
  value: string | number | null | undefined
): number => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    const abs = Math.abs(value);
    if (abs >= 1e12) return Math.floor(value);
    return Math.floor(value * 1000);
  }
  const trimmed = String(value).trim();
  if (!trimmed) return 0;
  const parsed = Date.parse(trimmed);
  if (!Number.isNaN(parsed)) return parsed;
  const asNum = Number(trimmed);
  if (!Number.isNaN(asNum) && asNum !== 0) {
    return lastUsedDateTimeToMillis(asNum);
  }
  return 0;
};

/** Normalizes to ISO-8601 for storage on `PaymentTypeOption.lastUsedDateTime`. */
export const normalizeLastUsedDateTimeToIso = (
  value: string | number | null | undefined
): string | null => {
  const ms = lastUsedDateTimeToMillis(value);
  if (ms <= 0) return null;
  return new Date(ms).toISOString();
};

/** Descending sort comparator (newest first). */
export const compareLastUsedDateTimeDesc = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number => lastUsedDateTimeToMillis(b) - lastUsedDateTimeToMillis(a);

/** Drop-in replacement for the duplicated `sortByLastUsed` helpers. */
export const sortByLastUsed = compareLastUsedDateTimeDesc;

export const sortBraintreeCardsByRecency = (
  cards: BraintreeCard[]
): BraintreeCard[] =>
  [...cards].sort((a, b) => {
    const diff = compareLastUsedDateTimeDesc(
      a.lastUsedDateTime,
      b.lastUsedDateTime
    );
    if (diff !== 0) return diff;
    return (b.default ? 1 : 0) - (a.default ? 1 : 0);
  });
